// src/components/Button.tsx
import React from "react";

// src/lib/cn.ts
import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";
function cn(...inputs) {
  return twMerge(clsx(inputs));
}

// src/components/Button.tsx
import { jsx, jsxs } from "react/jsx-runtime";
var variantStyles = {
  // Primary: Gold background, dark text (BOBY brand standard)
  primary: "bg-primary text-text-primary hover:bg-primary-dark focus:ring-primary/50 font-semibold",
  // Secondary: Grey background
  secondary: "bg-grey-200 text-text-primary hover:bg-grey-300 focus:ring-grey-300/50",
  // Outline: Gold border
  outline: "border-2 border-primary text-text-primary hover:bg-primary/10 focus:ring-primary/50",
  // Ghost: Text only
  ghost: "text-text-secondary hover:bg-grey-100 hover:text-text-primary focus:ring-grey-300/50",
  // Danger: Red for destructive actions
  danger: "bg-danger text-white hover:bg-red-700 focus:ring-danger/50"
};
var sizeStyles = {
  sm: "px-3 py-1.5 text-sm",
  md: "px-4 py-2 text-base",
  lg: "px-6 py-3 text-lg"
};
var Button = React.forwardRef(
  ({
    className,
    variant = "primary",
    size = "md",
    isLoading = false,
    fullWidth = false,
    disabled,
    children,
    ...props
  }, ref) => {
    return /* @__PURE__ */ jsxs(
      "button",
      {
        ref,
        disabled: disabled || isLoading,
        className: cn(
          // Base styles
          "inline-flex items-center justify-center font-medium rounded-lg",
          "transition-all duration-200 ease-in-out",
          "focus:outline-none focus:ring-2 focus:ring-offset-2",
          "disabled:opacity-50 disabled:cursor-not-allowed",
          // Variant styles
          variantStyles[variant],
          // Size styles
          sizeStyles[size],
          // Full width
          fullWidth && "w-full",
          className
        ),
        ...props,
        children: [
          isLoading && /* @__PURE__ */ jsxs(
            "svg",
            {
              className: "animate-spin -ml-1 mr-2 h-4 w-4",
              xmlns: "http://www.w3.org/2000/svg",
              fill: "none",
              viewBox: "0 0 24 24",
              children: [
                /* @__PURE__ */ jsx(
                  "circle",
                  {
                    className: "opacity-25",
                    cx: "12",
                    cy: "12",
                    r: "10",
                    stroke: "currentColor",
                    strokeWidth: "4"
                  }
                ),
                /* @__PURE__ */ jsx(
                  "path",
                  {
                    className: "opacity-75",
                    fill: "currentColor",
                    d: "M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  }
                )
              ]
            }
          ),
          children
        ]
      }
    );
  }
);
Button.displayName = "Button";

// src/components/Card.tsx
import React2 from "react";
import { jsx as jsx2 } from "react/jsx-runtime";
var variantStyles2 = {
  default: "bg-white border border-gray-200",
  elevated: "bg-white shadow-lg shadow-gray-200/50",
  outlined: "bg-transparent border-2 border-gray-300",
  glass: "bg-white/80 backdrop-blur-sm border border-white/20 shadow-lg"
};
var paddingStyles = {
  none: "",
  sm: "p-3",
  md: "p-4 sm:p-6",
  lg: "p-6 sm:p-8"
};
var Card = React2.forwardRef(
  ({ className, variant = "default", padding = "md", interactive = false, children, ...props }, ref) => {
    return /* @__PURE__ */ jsx2(
      "div",
      {
        ref,
        className: cn(
          "rounded-xl",
          variantStyles2[variant],
          paddingStyles[padding],
          interactive && "transition-all duration-200 hover:shadow-xl hover:-translate-y-0.5 cursor-pointer",
          className
        ),
        ...props,
        children
      }
    );
  }
);
Card.displayName = "Card";
var CardHeader = ({
  className,
  children,
  ...props
}) => /* @__PURE__ */ jsx2("div", { className: cn("mb-4", className), ...props, children });
var CardTitle = ({
  className,
  children,
  ...props
}) => /* @__PURE__ */ jsx2("h3", { className: cn("text-lg font-semibold text-gray-900", className), ...props, children });
var CardDescription = ({
  className,
  children,
  ...props
}) => /* @__PURE__ */ jsx2("p", { className: cn("text-sm text-gray-500 mt-1", className), ...props, children });
var CardContent = ({
  className,
  children,
  ...props
}) => /* @__PURE__ */ jsx2("div", { className: cn("", className), ...props, children });
var CardFooter = ({
  className,
  children,
  ...props
}) => /* @__PURE__ */ jsx2("div", { className: cn("mt-4 pt-4 border-t border-gray-100", className), ...props, children });

// src/components/CircleBadge.tsx
import React3 from "react";
import { jsx as jsx3, jsxs as jsxs2 } from "react/jsx-runtime";
var circleColors = {
  center: "bg-tier-1/20 text-tier-1 border-tier-1",
  // Gold - Highest trust
  inner: "bg-tier-2/20 text-tier-2 border-tier-2",
  // Blue
  mid: "bg-tier-3/20 text-tier-3 border-tier-3",
  // Green
  outer: "bg-tier-4/20 text-tier-4 border-tier-4",
  // Amber
  public: "bg-tier-5/20 text-tier-5 border-tier-5"
  // Grey
};
var circleLabels = {
  center: "Center",
  inner: "Inner",
  mid: "Mid",
  outer: "Outer",
  public: "Public"
};
var circleMarkers = {
  center: "C1",
  inner: "C2",
  mid: "C3",
  outer: "C4",
  public: "C5"
};
var sizeStyles2 = {
  sm: "text-xs px-2 py-0.5",
  md: "text-sm px-2.5 py-1",
  lg: "text-base px-3 py-1.5"
};
var CircleBadge = React3.forwardRef(
  ({ className, level, size = "md", showLabel = true, ...props }, ref) => {
    return /* @__PURE__ */ jsxs2(
      "span",
      {
        ref,
        className: cn(
          "inline-flex items-center font-medium rounded border",
          circleColors[level],
          sizeStyles2[size],
          className
        ),
        ...props,
        children: [
          /* @__PURE__ */ jsx3("span", { className: "font-bold mr-1.5", children: circleMarkers[level] }),
          showLabel && circleLabels[level]
        ]
      }
    );
  }
);
CircleBadge.displayName = "CircleBadge";

// src/components/Input.tsx
import React4 from "react";
import { jsx as jsx4, jsxs as jsxs3 } from "react/jsx-runtime";
var Input = React4.forwardRef(
  ({ className, label, helperText, error, leftAddon, rightAddon, id, ...props }, ref) => {
    const inputId = id || `input-${Math.random().toString(36).substr(2, 9)}`;
    return /* @__PURE__ */ jsxs3("div", { className: "w-full", children: [
      label && /* @__PURE__ */ jsx4(
        "label",
        {
          htmlFor: inputId,
          className: "block text-sm font-medium text-gray-700 mb-1.5",
          children: label
        }
      ),
      /* @__PURE__ */ jsxs3("div", { className: "relative", children: [
        leftAddon && /* @__PURE__ */ jsx4("div", { className: "absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-500", children: leftAddon }),
        /* @__PURE__ */ jsx4(
          "input",
          {
            ref,
            id: inputId,
            className: cn(
              "block w-full rounded-lg border bg-white px-4 py-2.5 text-gray-900",
              "placeholder:text-gray-400",
              "transition-colors duration-200",
              "focus:outline-none focus:ring-2 focus:ring-offset-0",
              error ? "border-boby-danger focus:border-boby-danger focus:ring-red-500/20" : "border-gray-300 focus:border-boby-primary focus:ring-boby-primary/20",
              leftAddon && "pl-10",
              rightAddon && "pr-10",
              "disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed",
              className
            ),
            ...props
          }
        ),
        rightAddon && /* @__PURE__ */ jsx4("div", { className: "absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none text-gray-500", children: rightAddon })
      ] }),
      (helperText || error) && /* @__PURE__ */ jsx4(
        "p",
        {
          className: cn(
            "mt-1.5 text-sm",
            error ? "text-boby-danger" : "text-gray-500"
          ),
          children: error || helperText
        }
      )
    ] });
  }
);
Input.displayName = "Input";

// src/components/Marker.tsx
import React5 from "react";
import { jsx as jsx5 } from "react/jsx-runtime";
var sizeStyles3 = {
  sm: "w-8 h-8 text-xs",
  md: "w-10 h-10 text-sm",
  lg: "w-12 h-12 text-base"
};
var variantStyles3 = {
  primary: "bg-primary text-text-primary",
  success: "bg-success/20 text-success",
  warning: "bg-warning/20 text-warning",
  danger: "bg-danger/20 text-danger",
  neutral: "bg-grey-200 text-text-primary"
};
var Marker = React5.forwardRef(
  ({ className, code, size = "md", variant = "primary", ...props }, ref) => {
    const displayCode = code.slice(0, 2).toUpperCase();
    return /* @__PURE__ */ jsx5(
      "div",
      {
        ref,
        className: cn(
          "rounded-lg flex items-center justify-center font-bold shrink-0",
          sizeStyles3[size],
          variantStyles3[variant],
          className
        ),
        ...props,
        children: displayCode
      }
    );
  }
);
Marker.displayName = "Marker";

// src/components/Select.tsx
import React6 from "react";
import { jsx as jsx6, jsxs as jsxs4 } from "react/jsx-runtime";
var Select = React6.forwardRef(
  ({ className, label, helperText, error, options, placeholder, id, ...props }, ref) => {
    const selectId = id || `select-${Math.random().toString(36).substr(2, 9)}`;
    return /* @__PURE__ */ jsxs4("div", { className: "w-full", children: [
      label && /* @__PURE__ */ jsx6(
        "label",
        {
          htmlFor: selectId,
          className: "block text-sm font-medium text-text-primary mb-1.5",
          children: label
        }
      ),
      /* @__PURE__ */ jsxs4("div", { className: "relative", children: [
        /* @__PURE__ */ jsxs4(
          "select",
          {
            ref,
            id: selectId,
            className: cn(
              "block w-full rounded-lg border bg-white px-4 py-2.5 pr-10 text-text-primary",
              "appearance-none cursor-pointer",
              "transition-colors duration-200",
              "focus:outline-none focus:ring-2 focus:ring-offset-0",
              error ? "border-danger focus:border-danger focus:ring-danger/20" : "border-grey-300 focus:border-primary focus:ring-primary/20",
              "disabled:bg-grey-100 disabled:text-text-muted disabled:cursor-not-allowed",
              className
            ),
            ...props,
            children: [
              placeholder && /* @__PURE__ */ jsx6("option", { value: "", disabled: true, children: placeholder }),
              options.map((option) => /* @__PURE__ */ jsx6(
                "option",
                {
                  value: option.value,
                  disabled: option.disabled,
                  children: option.label
                },
                option.value
              ))
            ]
          }
        ),
        /* @__PURE__ */ jsx6("div", { className: "pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3 text-text-muted", children: /* @__PURE__ */ jsx6("svg", { className: "h-4 w-4", fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", children: /* @__PURE__ */ jsx6("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M19 9l-7 7-7-7" }) }) })
      ] }),
      (helperText || error) && /* @__PURE__ */ jsx6(
        "p",
        {
          className: cn(
            "mt-1.5 text-sm",
            error ? "text-danger" : "text-text-muted"
          ),
          children: error || helperText
        }
      )
    ] });
  }
);
Select.displayName = "Select";

// src/components/Modal.tsx
import { useEffect, useCallback } from "react";
import { jsx as jsx7, jsxs as jsxs5 } from "react/jsx-runtime";
var sizeStyles4 = {
  sm: "max-w-md",
  md: "max-w-lg",
  lg: "max-w-2xl"
};
function Modal({
  isOpen,
  onClose,
  title,
  description,
  children,
  size = "md",
  showCloseButton = true,
  closeOnBackdrop = true
}) {
  const handleEscape = useCallback(
    (e) => {
      if (e.key === "Escape") {
        onClose();
      }
    },
    [onClose]
  );
  useEffect(() => {
    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "";
    };
  }, [isOpen, handleEscape]);
  if (!isOpen) return null;
  return /* @__PURE__ */ jsxs5("div", { className: "fixed inset-0 z-50 flex items-center justify-center p-4", children: [
    /* @__PURE__ */ jsx7(
      "div",
      {
        className: "absolute inset-0 bg-text-primary/50 backdrop-blur-sm animate-fade-in",
        onClick: closeOnBackdrop ? onClose : void 0,
        "aria-hidden": "true"
      }
    ),
    /* @__PURE__ */ jsxs5(
      "div",
      {
        className: cn(
          "relative w-full bg-white rounded-xl shadow-xl animate-slide-up",
          sizeStyles4[size]
        ),
        role: "dialog",
        "aria-modal": "true",
        "aria-labelledby": title ? "modal-title" : void 0,
        children: [
          (title || showCloseButton) && /* @__PURE__ */ jsxs5("div", { className: "flex items-start justify-between p-6 border-b border-grey-200", children: [
            /* @__PURE__ */ jsxs5("div", { children: [
              title && /* @__PURE__ */ jsx7("h2", { id: "modal-title", className: "text-lg font-bold text-text-primary", children: title }),
              description && /* @__PURE__ */ jsx7("p", { className: "mt-1 text-sm text-text-muted", children: description })
            ] }),
            showCloseButton && /* @__PURE__ */ jsx7(
              "button",
              {
                onClick: onClose,
                className: "p-1 rounded-lg text-text-muted hover:text-text-primary hover:bg-grey-100 transition-colors",
                "aria-label": "Close modal",
                children: /* @__PURE__ */ jsx7("svg", { className: "w-5 h-5", fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", children: /* @__PURE__ */ jsx7("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M6 18L18 6M6 6l12 12" }) })
              }
            )
          ] }),
          /* @__PURE__ */ jsx7("div", { className: "p-6", children })
        ]
      }
    )
  ] });
}
function ModalFooter({
  children,
  className
}) {
  return /* @__PURE__ */ jsx7(
    "div",
    {
      className: cn(
        "flex items-center justify-end gap-3 pt-4 mt-4 border-t border-grey-200",
        className
      ),
      children
    }
  );
}
function ConfirmModal({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = "Confirm",
  cancelText = "Cancel",
  variant = "default",
  isLoading = false
}) {
  return /* @__PURE__ */ jsxs5(Modal, { isOpen, onClose, title, size: "sm", children: [
    /* @__PURE__ */ jsx7("p", { className: "text-text-secondary", children: message }),
    /* @__PURE__ */ jsxs5(ModalFooter, { children: [
      /* @__PURE__ */ jsx7(Button, { variant: "ghost", onClick: onClose, disabled: isLoading, children: cancelText }),
      /* @__PURE__ */ jsx7(
        Button,
        {
          variant: variant === "danger" ? "danger" : "primary",
          onClick: onConfirm,
          isLoading,
          children: confirmText
        }
      )
    ] })
  ] });
}

// src/components/Toast.tsx
import { createContext, useContext, useState, useCallback as useCallback2 } from "react";
import { jsx as jsx8, jsxs as jsxs6 } from "react/jsx-runtime";
var ToastContext = createContext(void 0);
function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return context;
}
function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const addToast = useCallback2((toast) => {
    const id = Math.random().toString(36).substr(2, 9);
    const newToast = { ...toast, id };
    setToasts((prev) => [...prev, newToast]);
    const duration = toast.duration ?? 5e3;
    if (duration > 0) {
      setTimeout(() => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
      }, duration);
    }
  }, []);
  const removeToast = useCallback2((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);
  return /* @__PURE__ */ jsxs6(ToastContext.Provider, { value: { toasts, addToast, removeToast }, children: [
    children,
    /* @__PURE__ */ jsx8(ToastContainer, { toasts, onRemove: removeToast })
  ] });
}
var typeStyles = {
  success: "bg-success text-white",
  error: "bg-danger text-white",
  warning: "bg-warning text-text-primary",
  info: "bg-tier-2 text-white"
};
var typeMarkers = {
  success: "OK",
  error: "ER",
  warning: "WN",
  info: "IN"
};
function ToastContainer({
  toasts,
  onRemove
}) {
  if (toasts.length === 0) return null;
  return /* @__PURE__ */ jsx8("div", { className: "fixed bottom-4 right-4 z-50 flex flex-col gap-2 max-w-sm", children: toasts.map((toast) => /* @__PURE__ */ jsx8(ToastItem, { toast, onRemove }, toast.id)) });
}
function ToastItem({
  toast,
  onRemove
}) {
  return /* @__PURE__ */ jsxs6(
    "div",
    {
      className: cn(
        "flex items-start gap-3 p-4 rounded-lg shadow-lg animate-slide-up",
        typeStyles[toast.type]
      ),
      role: "alert",
      children: [
        /* @__PURE__ */ jsx8("div", { className: "w-8 h-8 rounded bg-white/20 flex items-center justify-center text-xs font-bold shrink-0", children: typeMarkers[toast.type] }),
        /* @__PURE__ */ jsxs6("div", { className: "flex-1 min-w-0", children: [
          /* @__PURE__ */ jsx8("p", { className: "font-semibold", children: toast.title }),
          toast.message && /* @__PURE__ */ jsx8("p", { className: "text-sm opacity-90 mt-0.5", children: toast.message })
        ] }),
        /* @__PURE__ */ jsx8(
          "button",
          {
            onClick: () => onRemove(toast.id),
            className: "p-1 rounded hover:bg-white/20 transition-colors shrink-0",
            "aria-label": "Dismiss",
            children: /* @__PURE__ */ jsx8("svg", { className: "w-4 h-4", fill: "none", stroke: "currentColor", viewBox: "0 0 24 24", children: /* @__PURE__ */ jsx8("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M6 18L18 6M6 6l12 12" }) })
          }
        )
      ]
    }
  );
}

// src/components/StatusBadge.tsx
import { jsx as jsx9, jsxs as jsxs7 } from "react/jsx-runtime";
var typeStyles2 = {
  success: "bg-green-100 text-green-800 border-green-200",
  warning: "bg-amber-100 text-amber-800 border-amber-200",
  error: "bg-red-100 text-red-800 border-red-200",
  info: "bg-blue-100 text-blue-800 border-blue-200",
  neutral: "bg-gray-100 text-gray-800 border-gray-200",
  pending: "bg-yellow-100 text-yellow-800 border-yellow-200",
  active: "bg-emerald-100 text-emerald-800 border-emerald-200",
  completed: "bg-green-100 text-green-800 border-green-200",
  cancelled: "bg-gray-100 text-gray-500 border-gray-200"
};
var dotStyles = {
  success: "bg-green-500",
  warning: "bg-amber-500",
  error: "bg-red-500",
  info: "bg-blue-500",
  neutral: "bg-gray-500",
  pending: "bg-yellow-500",
  active: "bg-emerald-500",
  completed: "bg-green-500",
  cancelled: "bg-gray-400"
};
var sizeStyles5 = {
  sm: "text-xs px-2 py-0.5",
  md: "text-sm px-2.5 py-1",
  lg: "text-base px-3 py-1.5"
};
function StatusBadge({
  children,
  type = "neutral",
  size = "md",
  className,
  showDot = false
}) {
  return /* @__PURE__ */ jsxs7(
    "span",
    {
      className: cn(
        "inline-flex items-center gap-1.5 font-medium rounded-full border",
        typeStyles2[type],
        sizeStyles5[size],
        className
      ),
      children: [
        showDot && /* @__PURE__ */ jsx9(
          "span",
          {
            className: cn(
              "w-2 h-2 rounded-full",
              dotStyles[type]
            )
          }
        ),
        children
      ]
    }
  );
}
function getStatusType(status) {
  const statusLower = status.toLowerCase();
  if (["open", "active", "available", "confirmed", "approved"].includes(statusLower)) {
    return "active";
  }
  if (["completed", "done", "finished", "paid", "accepted"].includes(statusLower)) {
    return "success";
  }
  if (["pending", "submitted", "waiting", "processing"].includes(statusLower)) {
    return "pending";
  }
  if (["cancelled", "closed", "expired", "inactive"].includes(statusLower)) {
    return "cancelled";
  }
  if (["rejected", "failed", "error", "declined"].includes(statusLower)) {
    return "error";
  }
  if (["urgent", "warning", "attention"].includes(statusLower)) {
    return "warning";
  }
  return "neutral";
}

// src/components/EmptyState.tsx
import { jsx as jsx10, jsxs as jsxs8 } from "react/jsx-runtime";
var sizeStyles6 = {
  sm: {
    container: "py-6 px-4",
    icon: "text-3xl mb-2",
    title: "text-base",
    description: "text-sm"
  },
  md: {
    container: "py-10 px-6",
    icon: "text-4xl mb-3",
    title: "text-lg",
    description: "text-sm"
  },
  lg: {
    container: "py-16 px-8",
    icon: "text-5xl mb-4",
    title: "text-xl",
    description: "text-base"
  }
};
function EmptyState({
  icon = "\u{1F4ED}",
  title,
  description,
  action,
  className,
  size = "md"
}) {
  const styles = sizeStyles6[size];
  return /* @__PURE__ */ jsxs8(
    "div",
    {
      className: cn(
        "flex flex-col items-center justify-center text-center",
        styles.container,
        className
      ),
      children: [
        icon && /* @__PURE__ */ jsx10("div", { className: cn("opacity-80", styles.icon), children: icon }),
        /* @__PURE__ */ jsx10("h3", { className: cn("font-semibold text-gray-900", styles.title), children: title }),
        description && /* @__PURE__ */ jsx10("p", { className: cn("text-gray-500 mt-1 max-w-sm", styles.description), children: description }),
        action && /* @__PURE__ */ jsx10("div", { className: "mt-4", children: action })
      ]
    }
  );
}
var EmptyStatePresets = {
  noJobs: {
    icon: "\u{1F4BC}",
    title: "No jobs found",
    description: "Try adjusting your filters or check back later for new opportunities."
  },
  noApplications: {
    icon: "\u{1F4CB}",
    title: "No applications yet",
    description: "Browse available jobs and submit your first application."
  },
  noEarnings: {
    icon: "\u{1F4B0}",
    title: "No earnings yet",
    description: "Complete shifts to start earning."
  },
  noNotifications: {
    icon: "\u{1F514}",
    title: "All caught up!",
    description: "No new notifications."
  },
  noAgents: {
    icon: "\u{1F465}",
    title: "No agents found",
    description: "No agents match your search criteria."
  },
  noResults: {
    icon: "\u{1F50D}",
    title: "No results found",
    description: "Try different search terms or filters."
  },
  error: {
    icon: "\u26A0\uFE0F",
    title: "Something went wrong",
    description: "We couldn't load this content. Please try again."
  }
};

// src/components/Skeleton.tsx
import { jsx as jsx11, jsxs as jsxs9 } from "react/jsx-runtime";
var roundedStyles = {
  none: "rounded-none",
  sm: "rounded-sm",
  md: "rounded-md",
  lg: "rounded-lg",
  full: "rounded-full"
};
function Skeleton({
  className,
  width,
  height,
  rounded = "md",
  animation = "pulse"
}) {
  const animationClass = animation === "pulse" ? "animate-pulse" : animation === "shimmer" ? "animate-shimmer bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 bg-[length:200%_100%]" : "";
  return /* @__PURE__ */ jsx11(
    "div",
    {
      className: cn(
        "bg-gray-200",
        roundedStyles[rounded],
        animationClass,
        className
      ),
      style: {
        width,
        height
      }
    }
  );
}
function SkeletonText({
  lines = 1,
  lastLineWidth = "60%",
  className
}) {
  return /* @__PURE__ */ jsx11("div", { className: cn("space-y-2", className), children: Array.from({ length: lines }).map((_, i) => /* @__PURE__ */ jsx11(
    Skeleton,
    {
      height: "1rem",
      width: i === lines - 1 && lines > 1 ? lastLineWidth : "100%",
      rounded: "sm"
    },
    i
  )) });
}
var avatarSizes = {
  sm: "w-8 h-8",
  md: "w-10 h-10",
  lg: "w-12 h-12",
  xl: "w-16 h-16"
};
function SkeletonAvatar({ size = "md", className }) {
  return /* @__PURE__ */ jsx11(
    Skeleton,
    {
      className: cn(avatarSizes[size], className),
      rounded: "full"
    }
  );
}
function SkeletonCard({
  showAvatar = false,
  lines = 3,
  className
}) {
  return /* @__PURE__ */ jsx11("div", { className: cn("bg-white rounded-lg border p-4", className), children: /* @__PURE__ */ jsxs9("div", { className: "flex gap-3", children: [
    showAvatar && /* @__PURE__ */ jsx11(SkeletonAvatar, {}),
    /* @__PURE__ */ jsxs9("div", { className: "flex-1", children: [
      /* @__PURE__ */ jsx11(Skeleton, { height: "1.25rem", width: "70%", rounded: "sm", className: "mb-2" }),
      /* @__PURE__ */ jsx11(SkeletonText, { lines })
    ] })
  ] }) });
}
function SkeletonTableRow({ columns = 4, className }) {
  return /* @__PURE__ */ jsx11("tr", { className, children: Array.from({ length: columns }).map((_, i) => /* @__PURE__ */ jsx11("td", { className: "px-4 py-3", children: /* @__PURE__ */ jsx11(Skeleton, { height: "1rem", width: i === 0 ? "80%" : "60%", rounded: "sm" }) }, i)) });
}
function SkeletonList({
  items = 3,
  showAvatar = false,
  className
}) {
  return /* @__PURE__ */ jsx11("div", { className: cn("space-y-3", className), children: Array.from({ length: items }).map((_, i) => /* @__PURE__ */ jsxs9("div", { className: "flex items-center gap-3", children: [
    showAvatar && /* @__PURE__ */ jsx11(SkeletonAvatar, { size: "sm" }),
    /* @__PURE__ */ jsx11("div", { className: "flex-1", children: /* @__PURE__ */ jsx11(Skeleton, { height: "0.875rem", width: `${80 - i * 10}%`, rounded: "sm" }) })
  ] }, i)) });
}

// src/components/DataTable.tsx
import { useState as useState2, useMemo } from "react";
import { jsx as jsx12, jsxs as jsxs10 } from "react/jsx-runtime";
function DataTable({
  data,
  columns,
  getRowKey,
  isLoading = false,
  emptyMessage = "No data available",
  onRowClick,
  className,
  hoverable = true,
  compact = false,
  striped = false
}) {
  const [sortKey, setSortKey] = useState2(null);
  const [sortDirection, setSortDirection] = useState2(null);
  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDirection(
        (prev) => prev === "asc" ? "desc" : prev === "desc" ? null : "asc"
      );
      if (sortDirection === "desc") {
        setSortKey(null);
      }
    } else {
      setSortKey(key);
      setSortDirection("asc");
    }
  };
  const sortedData = useMemo(() => {
    if (!sortKey || !sortDirection) return data;
    return [...data].sort((a, b) => {
      const aVal = a[sortKey];
      const bVal = b[sortKey];
      if (aVal === bVal) return 0;
      if (aVal === null || aVal === void 0) return 1;
      if (bVal === null || bVal === void 0) return -1;
      const comparison = aVal < bVal ? -1 : 1;
      return sortDirection === "desc" ? -comparison : comparison;
    });
  }, [data, sortKey, sortDirection]);
  const cellPadding = compact ? "px-3 py-2" : "px-4 py-3";
  return /* @__PURE__ */ jsx12("div", { className: cn("overflow-x-auto", className), children: /* @__PURE__ */ jsxs10("table", { className: "w-full border-collapse", children: [
    /* @__PURE__ */ jsx12("thead", { children: /* @__PURE__ */ jsx12("tr", { className: "border-b border-gray-200 bg-gray-50", children: columns.map((column) => /* @__PURE__ */ jsx12(
      "th",
      {
        className: cn(
          cellPadding,
          "text-left text-sm font-semibold text-gray-900",
          column.hideOnMobile && "hidden md:table-cell",
          column.align === "center" && "text-center",
          column.align === "right" && "text-right",
          column.sortable && "cursor-pointer select-none hover:bg-gray-100"
        ),
        style: { width: column.width },
        onClick: () => column.sortable && handleSort(column.key),
        children: /* @__PURE__ */ jsxs10("div", { className: "flex items-center gap-1", children: [
          column.label,
          column.sortable && /* @__PURE__ */ jsx12("span", { className: "text-gray-400", children: sortKey === column.key ? sortDirection === "asc" ? "\u2191" : sortDirection === "desc" ? "\u2193" : "\u2195" : "\u2195" })
        ] })
      },
      column.key
    )) }) }),
    /* @__PURE__ */ jsx12("tbody", { children: isLoading ? (
      // Loading skeleton rows
      Array.from({ length: 5 }).map((_, i) => /* @__PURE__ */ jsx12("tr", { className: "border-b border-gray-100", children: columns.map((column) => /* @__PURE__ */ jsx12(
        "td",
        {
          className: cn(cellPadding, column.hideOnMobile && "hidden md:table-cell"),
          children: /* @__PURE__ */ jsx12("div", { className: "h-4 bg-gray-200 rounded animate-pulse", style: { width: "60%" } })
        },
        column.key
      )) }, `skeleton-${i}`))
    ) : sortedData.length === 0 ? /* @__PURE__ */ jsx12("tr", { children: /* @__PURE__ */ jsx12(
      "td",
      {
        colSpan: columns.length,
        className: "px-4 py-12 text-center text-gray-500",
        children: emptyMessage
      }
    ) }) : sortedData.map((item, index) => /* @__PURE__ */ jsx12(
      "tr",
      {
        className: cn(
          "border-b border-gray-100 transition-colors",
          striped && index % 2 === 1 && "bg-gray-50",
          hoverable && "hover:bg-gray-50",
          onRowClick && "cursor-pointer"
        ),
        onClick: () => onRowClick?.(item, index),
        children: columns.map((column) => /* @__PURE__ */ jsx12(
          "td",
          {
            className: cn(
              cellPadding,
              "text-sm text-gray-900",
              column.hideOnMobile && "hidden md:table-cell",
              column.align === "center" && "text-center",
              column.align === "right" && "text-right"
            ),
            children: column.render ? column.render(item, index) : String(item[column.key] ?? "-")
          },
          column.key
        ))
      },
      getRowKey(item, index)
    )) })
  ] }) });
}

// src/components/Toggle.tsx
import { jsx as jsx13, jsxs as jsxs11 } from "react/jsx-runtime";
var sizeStyles7 = {
  sm: {
    track: "w-8 h-4",
    thumb: "w-3 h-3",
    translate: "translate-x-4"
  },
  md: {
    track: "w-11 h-6",
    thumb: "w-5 h-5",
    translate: "translate-x-5"
  },
  lg: {
    track: "w-14 h-8",
    thumb: "w-6 h-6",
    translate: "translate-x-6"
  }
};
function Toggle({
  checked,
  onChange,
  label,
  description,
  disabled = false,
  size = "md",
  className
}) {
  const styles = sizeStyles7[size];
  return /* @__PURE__ */ jsxs11(
    "label",
    {
      className: cn(
        "flex items-center gap-3 cursor-pointer",
        disabled && "opacity-50 cursor-not-allowed",
        className
      ),
      children: [
        /* @__PURE__ */ jsx13(
          "button",
          {
            type: "button",
            role: "switch",
            "aria-checked": checked,
            disabled,
            onClick: () => !disabled && onChange(!checked),
            className: cn(
              "relative inline-flex shrink-0 rounded-full transition-colors duration-200 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2",
              styles.track,
              checked ? "bg-amber-500" : "bg-gray-200",
              disabled && "cursor-not-allowed"
            ),
            children: /* @__PURE__ */ jsx13(
              "span",
              {
                className: cn(
                  "pointer-events-none inline-block rounded-full bg-white shadow-lg ring-0 transition-transform duration-200 ease-in-out",
                  styles.thumb,
                  "transform",
                  checked ? styles.translate : "translate-x-0.5",
                  "mt-0.5"
                )
              }
            )
          }
        ),
        (label || description) && /* @__PURE__ */ jsxs11("div", { className: "flex flex-col", children: [
          label && /* @__PURE__ */ jsx13("span", { className: "text-sm font-medium text-gray-900", children: label }),
          description && /* @__PURE__ */ jsx13("span", { className: "text-sm text-gray-500", children: description })
        ] })
      ]
    }
  );
}
export {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  CircleBadge,
  ConfirmModal,
  DataTable,
  EmptyState,
  EmptyStatePresets,
  Input,
  Marker,
  Modal,
  ModalFooter,
  Select,
  Skeleton,
  SkeletonAvatar,
  SkeletonCard,
  SkeletonList,
  SkeletonTableRow,
  SkeletonText,
  StatusBadge,
  ToastProvider,
  Toggle,
  cn,
  getStatusType,
  useToast
};
